import React from "react";
import { BytesUnit } from "@sscs/types";
import { LinearProgress, Tooltip } from "@sscs/ui";
import { getClassName, humanReadableBytes } from "@sscs/utils";
import styles from "./quotaBar.module.scss";

type QuotaBarLabel = {
  tooltip: string;
  suffix: string;
};

export type QuotaBarProps = {
  value: number;
  max: number;
  units?: BytesUnit;
  variant: "stacked" | "inline";
  warnValue?: number;
  extremeValue?: number;
  collapsed?: boolean;
  label?: QuotaBarLabel;
};

const MONTHLY_CAPACITY: QuotaBarLabel = {
  tooltip: "monthly analysis capacity used",
  suffix: "monthly capacity",
};

function percentage(value: number, max: number): number {
  if (value > 0 && max > 0) return Math.floor((value / max) * 100);
  else return 0;
}

function getUnitFromReadableValue(value: string) {
  return value.slice(-2);
}

function quotaColor(
  percentageValue: number,
  warnValue: number,
  extremeValue: number,
): "success" | "warning" | "error" {
  if (percentageValue >= extremeValue) return "error";
  if (percentageValue >= warnValue) return "warning";
  return "success";
}

const QuotaBar = ({
  value,
  max,
  units = "GB",
  variant,
  warnValue = 80,
  extremeValue = 90,
  collapsed = false,
  label = MONTHLY_CAPACITY,
}: QuotaBarProps): React.JSX.Element => {
  const percentageValue = Math.min(100, percentage(value, max));
  const readableValueFixedUnit = humanReadableBytes(value, 2, units, false);
  const readableMaxFixedUnit = humanReadableBytes(max, 2, units, false);
  const readableValueAnyUnit = humanReadableBytes(value, 0);
  const readableMaxAnyUnit = humanReadableBytes(max, 0);

  const tooltipReadableValue =
    getUnitFromReadableValue(readableValueAnyUnit) === units
      ? humanReadableBytes(value, 2, units)
      : readableValueAnyUnit;

  const tooltipReadableMax =
    getUnitFromReadableValue(readableMaxAnyUnit) === units
      ? humanReadableBytes(max, 2, units)
      : readableMaxAnyUnit;

  const color = quotaColor(percentageValue, warnValue, extremeValue);

  const values = `${readableValueFixedUnit} / ${readableMaxFixedUnit} ${units}`;

  return (
    <Tooltip
      trigger="graphic"
      title={`${
        value !== 0 ? tooltipReadableValue : readableValueFixedUnit
      } / ${tooltipReadableMax} ${label.tooltip} (${percentageValue}%)`}
    >
      <div className={getClassName(styles.root, styles[variant])}>
        {variant === "stacked" ? (
          <>
            <div className={styles.labels}>
              <span className={styles.percentage}>{percentageValue}%</span>
              {collapsed ? null : (
                <span className={styles.values}>{values}</span>
              )}
            </div>
            <LinearProgress
              progress={percentageValue}
              color={color}
              className={styles.track}
            />
          </>
        ) : (
          <>
            <div className={styles["inline-track"]}>
              <LinearProgress
                progress={percentageValue}
                color={color}
                className={styles.track}
              />
            </div>
            <span className={styles.values}>
              {values} <span className={styles.suffix}>{label.suffix}</span>
            </span>
          </>
        )}
      </div>
    </Tooltip>
  );
};

export default QuotaBar;
